"use client";

import dynamic from "next/dynamic";
import { useMemo, useState } from "react";
import type {
  AllStoriesPageData,
  ContentTypeFilter,
  ExploreBriefCard,
  ExploreStoryCard,
  TimeRangeFilter,
} from "../types";
import { CompactBriefRow } from "./compact-brief-row";
import { MobileStoryTextCard } from "./mobile-story-text-card";
import { StoryGridCard } from "./story-grid-card";

const CONTENT_TYPE_TO_MATERIAL: Record<ContentTypeFilter, string | null> = {
  all: null,
  news: "NEWS",
  workflow: "WORKFLOW",
  release: "RELEASE",
  prompt: "PROMPT",
  deal: "DEAL",
};

const LazyBriefDetailDialog = dynamic(
  async () => {
    const m = await import("./brief-detail-dialog");
    return m.BriefDetailDialog;
  },
  { ssr: false }
);

const TIME_RANGE_MS: Record<Exclude<TimeRangeFilter, "all">, number> = {
  "48h": 48 * 60 * 60 * 1000,
  "7d": 7 * 24 * 60 * 60 * 1000,
  "30d": 30 * 24 * 60 * 60 * 1000,
};

type OrderedSearchItem =
  | { kind: "story"; story: ExploreStoryCard; score: number }
  | { kind: "brief"; brief: ExploreBriefCard; score: number };

function isWithinTimeRange(
  reportDate: string,
  timeRange: TimeRangeFilter
): boolean {
  if (timeRange === "all") {
    return true;
  }
  const ts = new Date(reportDate).getTime();
  if (Number.isNaN(ts)) {
    return false;
  }
  return Date.now() - ts <= TIME_RANGE_MS[timeRange];
}

/**
 * Flat relevance-ordered list rendered when a search is active. Stories
 * and briefs are interleaved by RRF score and the matched query terms are
 * highlighted in titles via `<HighlightedText>`.
 *
 * Time filter is applied here client-side because the search service
 * returns a flat relevance-ordered list and time-range narrowing doesn't
 * reorder it.
 */
export function StoriesSearchResults({
  data,
  exploreBase,
  contentType = "all",
  selectedCategorySlugs = [],
  selectedTagSlugs = [],
  selectedToolSlugs = [],
  timeRange = "all",
  highlightQuery,
  onResetFilters,
}: {
  data: AllStoriesPageData;
  exploreBase: string;
  contentType?: ContentTypeFilter;
  selectedCategorySlugs?: string[];
  selectedTagSlugs?: string[];
  selectedToolSlugs?: string[];
  timeRange?: TimeRangeFilter;
  highlightQuery?: string;
  onResetFilters?: () => void;
}) {
  const search = data.search;
  const storiesById = useMemo(() => {
    const m = new Map<string, (typeof data.stories)[number]>();
    for (const s of data.stories) {
      m.set(`${s.reportDate}|${s.storyId}`, s);
    }
    return m;
  }, [data.stories]);
  const briefsById = useMemo(() => {
    const m = new Map<string, NonNullable<typeof data.briefs>[number]>();
    for (const b of data.briefs ?? []) {
      m.set(`${b.reportDate}|${b.briefId}`, b);
    }
    return m;
  }, [data.briefs]);

  const [openBrief, setOpenBrief] = useState<ExploreBriefCard | null>(null);

  if (!search) {
    return null;
  }

  const wantedMaterial = CONTENT_TYPE_TO_MATERIAL[contentType];
  const categorySet =
    selectedCategorySlugs.length > 0 ? new Set(selectedCategorySlugs) : null;
  const tagSet = selectedTagSlugs.length > 0 ? new Set(selectedTagSlugs) : null;
  const toolSet =
    selectedToolSlugs.length > 0 ? new Set(selectedToolSlugs) : null;

  const matchesStorySlugFilters = (story: ExploreStoryCard): boolean => {
    if (
      categorySet &&
      !(story.categorySlug && categorySet.has(story.categorySlug))
    ) {
      return false;
    }
    if (tagSet && !story.allTagSlugs.some((s) => tagSet.has(s))) {
      return false;
    }
    if (toolSet && !story.allTagSlugs.some((s) => toolSet.has(s))) {
      return false;
    }
    return true;
  };

  const matchesBriefSlugFilters = (brief: ExploreBriefCard): boolean => {
    // Briefs are excluded entirely whenever a Type filter is active —
    // material_type is a story-only concept. Server already enforces this
    // by setting `briefsHiddenByTypeFilter`, but be defensive.
    if (wantedMaterial !== null) {
      return false;
    }
    if (
      categorySet &&
      !brief.allCategorySlugs.some((s) => categorySet.has(s))
    ) {
      return false;
    }
    if (tagSet && !brief.allTagSlugs.some((s) => tagSet.has(s))) {
      return false;
    }
    if (toolSet && !brief.allTagSlugs.some((s) => toolSet.has(s))) {
      return false;
    }
    return true;
  };

  const orderedItems: OrderedSearchItem[] = [];
  for (const item of search.items) {
    if (item.kind === "story") {
      const story = storiesById.get(`${item.reportDate}|${item.storyId}`);
      if (!story) {
        continue;
      }
      if (!isWithinTimeRange(story.publishedAt, timeRange)) {
        continue;
      }
      if (wantedMaterial !== null && story.materialType !== wantedMaterial) {
        continue;
      }
      if (!matchesStorySlugFilters(story)) {
        continue;
      }
      orderedItems.push({ kind: "story", story, score: item.score });
      continue;
    }

    const brief = briefsById.get(`${item.reportDate}|${item.briefId}`);
    if (!brief) {
      continue;
    }
    if (!isWithinTimeRange(brief.reportDate, timeRange)) {
      continue;
    }
    if (!matchesBriefSlugFilters(brief)) {
      continue;
    }
    orderedItems.push({ kind: "brief", brief, score: item.score });
  }

  if (orderedItems.length === 0) {
    const hasActiveFacets =
      contentType !== "all" ||
      timeRange !== "all" ||
      selectedCategorySlugs.length > 0 ||
      selectedTagSlugs.length > 0 ||
      selectedToolSlugs.length > 0;
    return (
      <div className="rounded-2xl border border-[var(--explore-border-light)] bg-[var(--explore-bg-card)] px-6 py-10">
        <p
          className="font-bold text-[1.4rem] text-[var(--explore-text-primary)] leading-tight"
          style={{ fontFamily: "var(--explore-card-title-stack)" }}
        >
          No results for “{search.query}”
        </p>
        <p className="mt-2 max-w-prose text-[0.95rem] text-[var(--explore-text-secondary)]">
          {hasActiveFacets
            ? "Try a different spelling, fewer words, or clear the active filters."
            : "Try a different spelling or fewer words."}
        </p>
        {hasActiveFacets && onResetFilters && (
          <button
            className="mt-5 inline-flex items-center rounded-full bg-[var(--explore-text-primary)] px-4 py-2 font-medium text-[0.85rem] text-[var(--explore-bg-card)] transition-opacity hover:opacity-90"
            onClick={onResetFilters}
            type="button"
          >
            Clear filters
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <p
        className="mb-1 text-[1rem] text-[var(--explore-text-primary)]"
        style={{ fontFamily: "var(--homepage-sans-stack)" }}
      >
        <span className="font-semibold">{orderedItems.length}</span>{" "}
        {orderedItems.length === 1 ? "result" : "results"} for{" "}
        <span className="font-semibold">“{search.query}”</span>
      </p>
      <p
        className="mb-4 text-[0.78rem] text-[var(--explore-text-muted)]"
        style={{ fontFamily: "var(--homepage-sans-stack)" }}
      >
        Sorted by relevance
        {search.briefsHiddenByTypeFilter
          ? " · briefs hidden while a Type filter is active"
          : ""}
      </p>

      {/* Mobile: stack of full story cards and compact brief rows */}
      <div className="flex flex-col gap-3 px-1 lg:hidden">
        {orderedItems.map((it, idx) =>
          it.kind === "story" ? (
            <MobileStoryTextCard
              exploreBase={exploreBase}
              highlightQuery={highlightQuery}
              key={`s-${it.story.storyId}`}
              showDivider={false}
              story={it.story}
            />
          ) : (
            <BriefRowSurface key={`b-${it.brief.briefId}`}>
              <CompactBriefRow
                brief={it.brief}
                highlightQuery={highlightQuery}
                onClick={() => setOpenBrief(it.brief)}
                positionInList={idx}
                surface="stories_index_briefs"
              />
            </BriefRowSurface>
          )
        )}
      </div>

      {/* Desktop: stack of full story cards and compact brief rows */}
      <div className="hidden flex-col gap-3 lg:flex">
        {orderedItems.map((it, idx) =>
          it.kind === "story" ? (
            <StoryGridCard
              exploreBase={exploreBase}
              highlightQuery={highlightQuery}
              key={`s-${it.story.storyId}`}
              story={it.story}
            />
          ) : (
            <BriefRowSurface key={`b-${it.brief.briefId}`}>
              <CompactBriefRow
                brief={it.brief}
                highlightQuery={highlightQuery}
                onClick={() => setOpenBrief(it.brief)}
                positionInList={idx}
                surface="stories_index_briefs"
              />
            </BriefRowSurface>
          )
        )}
      </div>

      {openBrief && (
        <LazyBriefDetailDialog
          brief={openBrief}
          exploreBase={exploreBase}
          onOpenChange={(open) => {
            if (!open) {
              setOpenBrief(null);
            }
          }}
          open={openBrief !== null}
        />
      )}
    </div>
  );
}

/**
 * Wrap each compact brief row in the same bordered surface the default feed
 * uses (so the row stays visually grouped as a brief, distinct from stories).
 */
function BriefRowSurface({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-[var(--explore-border-light)] bg-[var(--explore-bg-card)]">
      {children}
    </div>
  );
}
